import React, { useState } from 'react';
import { FaEnvelope, FaUser, FaCheck, FaSpinner } from 'react-icons/fa';
import { subscribeToBlog } from '../api/client';
import { getAnonSessionId, getPendingTripContext } from '../utils/tripContext';

// `destination` is passed by the article that renders this form; on the blog
// index there is no single destination, so the drip falls back to whatever the
// reader last showed interest in, and to nothing if there is none.
const BlogSubscribeForm = ({ compact = false, destination }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await subscribeToBlog(email.trim(), firstName.trim(), {
        sessionId: getAnonSessionId(),
        destination: destination || getPendingTripContext()?.destination || null,
      });
      setSuccess(true);
      setEmail('');
      setFirstName('');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to subscribe. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={`bg-green-50 border border-green-200 rounded-xl p-6 text-center ${compact ? 'my-6' : 'my-10'}`}>
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <FaCheck className="text-green-600 text-xl" />
        </div>
        <h3 className="text-lg font-semibold text-green-800 mb-2">You're subscribed!</h3>
        <p className="text-green-700 text-sm">
          You'll receive an email whenever we publish a new travel article.
        </p>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-xl p-5 my-6">
        <h3 className="text-lg font-bold text-neutral-800 mb-2 flex items-center gap-2">
          <FaEnvelope className="text-primary-600" />
          Subscribe for Updates
        </h3>
        <p className="text-sm text-neutral-600 mb-4">
          Get notified when we publish new travel guides and tips.
        </p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm" />
              <input
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-neutral-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-sm"
              />
            </div>
            <div className="relative flex-1">
              <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm" />
              <input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-neutral-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-sm"
              />
            </div>
          </div>
          {error && <p className="text-red-600 text-xs">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-600 text-white py-2.5 rounded-lg font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin" /> Subscribing...
              </>
            ) : (
              'Subscribe'
            )}
          </button>
        </form>
        <p className="text-xs text-neutral-500 mt-3 text-center">
          No spam, ever. Unsubscribe anytime.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-primary-50 via-secondary-50 to-adventure-50 rounded-2xl p-8 my-10 border border-primary-100">
      <div className="max-w-lg mx-auto text-center">
        <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
          <FaEnvelope className="text-primary-600 text-2xl" />
        </div>
        <h3 className="text-2xl font-bold text-neutral-800 mb-2">
          Stay Updated with Travel Tips
        </h3>
        <p className="text-neutral-600 mb-6">
          Subscribe to our newsletter and be the first to know when we publish new travel guides, destination itineraries, and expert tips.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <FaUser className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                placeholder="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-neutral-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-base"
              />
            </div>
            <div className="relative flex-1">
              <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full pl-11 pr-4 py-3 rounded-xl border border-neutral-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-base"
              />
            </div>
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto px-8 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 mx-auto"
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin" /> Subscribing...
              </>
            ) : (
              'Subscribe to Newsletter'
            )}
          </button>
        </form>
        <p className="text-xs text-neutral-500 mt-4">
          We respect your privacy. Unsubscribe at any time with one click.
        </p>
      </div>
    </div>
  );
};

export default BlogSubscribeForm;
